import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send, Loader2, Trees } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const ChatAssistant = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setMessages([{ role: "assistant", content: t('chat.greeting') }]);
  }, [i18n.language, t]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMessage, session_id: sessionId })
      });

      if (!response.ok) throw new Error("Chat error");
      
      const data = await response.json();
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: "assistant", content: t('chat.error') }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#1B4D3E] text-white shadow-lg hover:bg-[#163D32] transition-all flex items-center justify-center ${isOpen ? 'hidden' : ''}`}
        data-testid="chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col" 
          style={{ height: "500px" }}
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-stone-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1B4D3E] flex items-center justify-center">
                <Trees className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-sm">{t('chat.title')}</h3>
                <p className="text-xs text-stone-500">{t('chat.subtitle')}</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)} 
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              data-testid="chat-close"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div 
                key={index} 
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                  msg.role === "user" 
                    ? "bg-[#1B4D3E] text-white rounded-br-md" 
                    : "bg-stone-100 text-stone-700 rounded-bl-md"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-stone-100 px-4 py-2.5 rounded-2xl rounded-bl-md">
                  <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-stone-100">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('chat.placeholder')}
                className="flex-1 h-10 rounded-xl border-stone-200"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                disabled={isLoading || !input.trim()}
                className="bg-[#1B4D3E] hover:bg-[#163D32] h-10 w-10 p-0 rounded-xl"
                data-testid="chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
